import { useState, useCallback } from "react";
import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Mail, MapPin, Send, Loader2 } from "lucide-react";
import { Header, Footer } from "../components/Layout";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Animation variants - extracted to prevent re-renders
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerChildren = {
  visible: { transition: { staggerChildren: 0.1 } }
};

const heroAnimation = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { delay: 0.2 }
};

const heroTitleAnimation = {
  initial: { opacity: 0, y: 50 },
  animate: { opacity: 1, y: 0 },
  transition: { delay: 0.3, duration: 0.8 }
};

const heroTextAnimation = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  transition: { delay: 0.5 }
};

// Contact Form Component
const ContactForm = () => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    
    // Simulate sending (no backend endpoint for general contact)
    setTimeout(() => {
      toast.success("Message sent! We'll get back to you soon.");
      setFormData({ name: "", email: "", subject: "", message: "" });
      setLoading(false);
    }, 1000);
  }, [formData]);

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label className="font-mono text-xs uppercase tracking-wider">Name *</Label>
          <Input
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            className="bg-[#0F0F0F] border-[#262626] rounded-none h-14 mt-2"
            required
            data-testid="contact-name-input"
          />
        </div>
        <div>
          <Label className="font-mono text-xs uppercase tracking-wider">Email *</Label>
          <Input
            name="email"
            type="email"
            value={formData.email}
            onChange={handleInputChange}
            className="bg-[#0F0F0F] border-[#262626] rounded-none h-14 mt-2"
            required
            data-testid="contact-email-input"
          />
        </div>
      </div>

      <div>
        <Label className="font-mono text-xs uppercase tracking-wider">Subject</Label>
        <Input
          name="subject"
          value={formData.subject}
          onChange={handleInputChange}
          className="bg-[#0F0F0F] border-[#262626] rounded-none h-14 mt-2"
          placeholder="e.g., Seraph Inquiry, Press Request, Partnership"
          data-testid="contact-subject-input"
        />
      </div>

      <div>
        <Label className="font-mono text-xs uppercase tracking-wider">Message *</Label>
        <Textarea
          name="message"
          value={formData.message}
          onChange={handleInputChange}
          className="bg-[#0F0F0F] border-[#262626] rounded-none mt-2 min-h-[150px]"
          required
          data-testid="contact-message-input"
        />
      </div>

      <button 
        type="submit"
        disabled={loading}
        className="w-full font-mono text-sm uppercase tracking-wider bg-[#FF2A00] text-white py-4 hover:bg-white hover:text-black transition-colors duration-200 disabled:opacity-50 flex items-center justify-center gap-2"
        data-testid="contact-submit-btn"
      >
        {loading ? (
          <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</>
        ) : (
          <><Send className="w-4 h-4" /> Send Message</>
        )}
      </button>
    </form>
  );
};

// Newsletter Form Component
const NewsletterForm = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleEmailChange = useCallback((e) => {
    setEmail(e.target.value);
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success("Subscribed to the uprising!");
      setEmail("");
    } catch (error) {
      console.warn("Newsletter subscription failed:", error.message);
      toast.error("Failed to subscribe. Try again.");
    } finally {
      setLoading(false);
    }
  }, [email]);

  return (
    <form onSubmit={handleSubmit} className="flex gap-3">
      <Input
        type="email"
        value={email}
        onChange={handleEmailChange}
        placeholder="your@email.com"
        className="bg-[#0F0F0F] border-[#262626] rounded-none h-14 flex-1"
        required
        data-testid="newsletter-email-input"
      />
      <button 
        type="submit"
        disabled={loading}
        className="font-mono text-sm uppercase tracking-wider bg-[#FF2A00] text-white px-6 hover:bg-white hover:text-black transition-colors duration-200"
        data-testid="newsletter-submit-btn"
      >
        {loading ? "..." : "Join"}
      </button>
    </form>
  );
};

// Direct Contact Section
const DirectContact = () => (
  <motion.div variants={fadeInUp} className="mb-12">
    <h2 className="font-display text-2xl mb-6">DIRECT CONTACT</h2>
    <div className="space-y-4">
      <a 
        href="mailto:taylor@keller-motors.com"
        className="flex items-center gap-4 p-4 border border-[#262626] hover:border-[#FF2A00] transition-colors"
      >
        <Mail className="w-5 h-5 text-[#FF2A00]" />
        <span>taylor@keller-motors.com</span>
      </a>
      <div className="flex items-center gap-4 p-4 border border-[#262626]">
        <MapPin className="w-5 h-5 text-[#FF2A00]" />
        <span className="text-[#A3A3A3]">Vaihingen an der Enz, Germany</span>
      </div>
    </div>
  </motion.div>
);

// Social Links Section
const SocialLinks = () => (
  <motion.div variants={fadeInUp} className="mb-12">
    <h2 className="font-display text-2xl mb-6">FOLLOW THE REVOLUTION</h2>
    <div className="grid grid-cols-3 gap-4">
      <SocialLink href="https://instagram.com/kellermotorsofficial" platform="Instagram" handle="@kellermotorsofficial" />
      <SocialLink href="https://facebook.com/kellercars" platform="Facebook" handle="@kellercars" />
      <SocialLink href="https://www.linkedin.com/company/keller-motors-inc./" platform="LinkedIn" handle="Keller Motors" />
    </div>
  </motion.div>
);

const SocialLink = ({ href, platform, handle }) => (
  <a 
    href={href} 
    target="_blank" 
    rel="noopener noreferrer"
    className="p-4 border border-[#262626] hover:border-[#FF2A00] transition-colors text-center"
  >
    <p className="font-mono text-xs uppercase tracking-wider text-[#A3A3A3]">{platform}</p>
    <p className="text-sm mt-1">{handle}</p>
  </a>
);

// Newsletter Section
const NewsletterSection = () => (
  <motion.div variants={fadeInUp} className="p-8 bg-[#0F0F0F] border border-[#262626]">
    <h2 className="font-display text-2xl mb-4">JOIN THE REVOLUTION</h2>
    <p className="text-[#A3A3A3] text-sm mb-6">
      Subscribe for exclusive updates, build progress, and insider content not available anywhere else.
    </p>
    <NewsletterForm />
  </motion.div>
);

// Hero Section
const HeroSection = () => (
  <section className="pt-32 pb-16 md:pt-40 md:pb-24 px-6 md:px-8" data-testid="contact-hero">
    <div className="max-w-[1400px] mx-auto">
      <motion.p 
        className="font-mono text-xs md:text-sm tracking-[0.2em] uppercase text-[#FF2A00] mb-4"
        initial={heroAnimation.initial}
        animate={heroAnimation.animate}
        transition={heroAnimation.transition}
      >
        Get in Touch
      </motion.p>
      <motion.h1 
        className="font-display text-5xl md:text-7xl lg:text-8xl tracking-tight mb-6"
        initial={heroTitleAnimation.initial}
        animate={heroTitleAnimation.animate}
        transition={heroTitleAnimation.transition}
      >
        CONTACT
      </motion.h1>
      <motion.p 
        className="text-lg md:text-xl text-[#A3A3A3] max-w-2xl"
        initial={heroTextAnimation.initial}
        animate={heroTextAnimation.animate}
        transition={heroTextAnimation.transition}
      >
        Questions about our vehicles? Press inquiries? Partnership opportunities? 
        We'd love to hear from you.
      </motion.p>
    </div>
  </section>
);

// Content Section
const ContentSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section ref={ref} className="pb-24 md:pb-40 px-6 md:px-8" data-testid="contact-content">
      <div className="max-w-[1400px] mx-auto grid lg:grid-cols-2 gap-16">
        {/* Contact Form */}
        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={staggerChildren}
        >
          <motion.h2 variants={fadeInUp} className="font-display text-2xl mb-8">
            SEND A MESSAGE
          </motion.h2>
          <motion.div variants={fadeInUp}>
            <ContactForm />
          </motion.div>
        </motion.div>

        {/* Info */}
        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={staggerChildren}
        >
          <DirectContact />
          <SocialLinks />
          <NewsletterSection />
        </motion.div>
      </div>
    </section>
  );
};

// Main Component
export default function ContactPage() {
  return (
    <main className="min-h-screen bg-[#050505]">
      <Header />
      <HeroSection />
      <ContentSection />
      <Footer />
    </main>
  );
}
